import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { getFreshToken } from '@/lib/auth-token';

interface Municipio {
  id: string;
  nome: string;
  sigla: string | null;
  ativo: boolean;
  created_at: string;
}

interface MunicipioFormData {
  nome: string;
  sigla: string;
  ativo: boolean;
}

interface UserFormData {
  email: string;
  password: string;
  full_name: string;
  role: 'admin' | 'viewer';
  municipio_id: string;
}

interface MunicipioStats {
  municipio_id: string;
  total_funcionarios: number;
  total_utilizadores: number;
}

// v2 - multi-tenant support
const ADMIN_USERS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-manage-users`;

export default function MunicipiosTab() {
  const { isSuperAdmin } = useAuth();
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [stats, setStats] = useState<Record<string, MunicipioStats>>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MunicipioFormData>({ nome: '', sigla: '', ativo: true });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Estado para criar utilizador rápido
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState<UserFormData>({
    email: '', password: '', full_name: '', role: 'admin', municipio_id: '',
  });
  const [savingUser, setSavingUser] = useState(false);

  const fetchMunicipios = useCallback(async () => {
    setLoading(true);
    try {
      const { data: muns } = await supabase
        .from('municipios')
        .select('*')
        .order('nome');

      setMunicipios(muns ?? []);

      if (muns && muns.length > 0) {
        const [{ data: funcs }, { data: profs }] = await Promise.all([
          supabase.from('funcionarios').select('municipio_id'),
          supabase.from('profiles').select('municipio_id'),
        ]);

        const statsMap: Record<string, MunicipioStats> = {};
        muns.forEach((m) => {
          statsMap[m.id] = {
            municipio_id: m.id,
            total_funcionarios: (funcs ?? []).filter((f) => f.municipio_id === m.id).length,
            total_utilizadores: (profs ?? []).filter((p) => p.municipio_id === m.id).length,
          };
        });
        setStats(statsMap);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMunicipios(); }, [fetchMunicipios]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ nome: '', sigla: '', ativo: true });
    setShowForm(true);
  };

  const openEdit = (m: Municipio) => {
    setEditingId(m.id);
    setForm({ nome: m.nome, sigla: m.sigla ?? '', ativo: m.ativo });
    setShowForm(true);
  };

  const openUserForm = (municipioId?: string) => {
    setUserForm({
      email: '',
      password: '',
      full_name: '',
      role: 'admin',
      municipio_id: municipioId ?? (municipios[0]?.id ?? ''),
    });
    setShowUserForm(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) {
      setMsg({ type: 'error', text: 'O nome do município é obrigatório.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const token = await getFreshToken();

      const action = editingId ? 'update_municipio' : 'create_municipio';
      const body: Record<string, unknown> = {
        action,
        nome: form.nome.trim(),
        sigla: form.sigla.trim() || null,
        ativo: form.ativo,
      };
      if (editingId) body.municipio_id = editingId;

      const res = await fetch(ADMIN_USERS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Erro ${res.status}`);

      setMsg({ type: 'success', text: editingId ? 'Módulo atualizado com sucesso!' : 'Módulo criado com sucesso!' });
      setShowForm(false);
      setEditingId(null);
      await fetchMunicipios();
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao guardar.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 5000);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.password || !userForm.full_name || !userForm.municipio_id) {
      setMsg({ type: 'error', text: 'Preenche todos os campos obrigatórios.' });
      return;
    }
    setSavingUser(true);
    setMsg(null);
    try {
      const token = await getFreshToken();

      const res = await fetch(ADMIN_USERS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: 'create',
          email: userForm.email,
          password: userForm.password,
          full_name: userForm.full_name,
          role: userForm.role,
          municipio_id: userForm.municipio_id,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Erro ${res.status}`);

      setMsg({ type: 'success', text: `Utilizador "${userForm.full_name}" criado com sucesso!` });
      setShowUserForm(false);
      await fetchMunicipios();
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao criar utilizador.' });
    } finally {
      setSavingUser(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  const handleToggleAtivo = async (m: Municipio) => {
    try {
      const token = await getFreshToken();

      await fetch(ADMIN_USERS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'toggle_municipio', municipio_id: m.id, ativo: !m.ativo }),
      });

      await fetchMunicipios();
    } catch {
      // silently fail
    }
  };

  const handleDelete = async (municipioId: string) => {
    setDeletingId(municipioId);
    try {
      const token = await getFreshToken();

      const res = await fetch(ADMIN_USERS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'delete_municipio', municipio_id: municipioId }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `Erro ${res.status}`);

      setMsg({ type: 'success', text: 'Módulo eliminado com sucesso!' });
      setConfirmDeleteId(null);
      await fetchMunicipios();
    } catch (err: unknown) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao eliminar.' });
    } finally {
      setDeletingId(null);
      setTimeout(() => setMsg(null), 5000);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-100 p-12 text-center">
        <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 rounded-full mx-auto mb-3">
          <i className="ri-lock-line text-zinc-400 text-xl" />
        </div>
        <p className="text-sm font-medium text-zinc-600">Apenas Super Admins podem gerir municípios.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Feedback */}
      {msg && (
        <div className={`flex items-center gap-2.5 p-3.5 rounded-xl border ${msg.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className={`text-sm ${msg.type === 'success' ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-error-warning-line text-red-500'}`} />
          </div>
          <p className={`text-[13px] ${msg.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>{msg.text}</p>
        </div>
      )}

      {/* Lista de municípios */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
              <i className="ri-building-4-line text-zinc-600 text-sm" />
            </div>
            <div>
              <h2 className="text-[14px] font-semibold text-zinc-800">Módulos / Municípios</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {municipios.length} módulo{municipios.length !== 1 ? 's' : ''} registado{municipios.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openUserForm()}
              className="flex items-center gap-2 px-3 py-2 border border-zinc-200 text-zinc-700 text-[13px] font-medium rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-add-line text-sm" />
              </div>
              Novo Utilizador
            </button>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-sm" />
              </div>
              Novo Módulo
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-14">
            <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
          </div>
        ) : municipios.length === 0 ? (
          <div className="py-14 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 rounded-full mx-auto mb-3">
              <i className="ri-building-4-line text-zinc-400 text-xl" />
            </div>
            <p className="text-sm text-zinc-500">Nenhum módulo registado.</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {municipios.map((m) => {
              const s = stats[m.id];
              const isPrincipal = m.id === '00000000-0000-0000-0000-000000000001';
              return (
                <div key={m.id} className="flex items-center justify-between px-6 py-4 hover:bg-zinc-50/60 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 font-bold text-sm ${m.ativo ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-400'}`}>
                      {m.sigla ? m.sigla.slice(0, 2).toUpperCase() : <i className="ri-building-4-line text-base" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-semibold text-zinc-800">{m.nome}</p>
                        {isPrincipal && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full">Principal</span>
                        )}
                        {!m.ativo && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold bg-zinc-100 text-zinc-500 rounded-full">Inativo</span>
                        )}
                      </div>
                      {m.sigla && (
                        <p className="text-[11px] text-zinc-400 mt-0.5">Sigla: {m.sigla}</p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 mx-4">
                    <div className="text-center hidden sm:block">
                      <p className="text-[15px] font-bold text-zinc-800">{s?.total_funcionarios ?? 0}</p>
                      <p className="text-[10px] text-zinc-400">Funcionários</p>
                    </div>
                    <div className="text-center hidden sm:block">
                      <p className="text-[15px] font-bold text-zinc-800">{s?.total_utilizadores ?? 0}</p>
                      <p className="text-[10px] text-zinc-400">Utilizadores</p>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openUserForm(m.id)}
                      title="Criar utilizador para este módulo"
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-user-add-line text-sm" />
                    </button>
                    <button
                      onClick={() => handleToggleAtivo(m)}
                      title={m.ativo ? 'Desativar' : 'Ativar'}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${m.ativo ? 'text-emerald-500 hover:bg-emerald-50' : 'text-zinc-400 hover:bg-zinc-100'}`}
                    >
                      <i className={m.ativo ? 'ri-toggle-fill text-base' : 'ri-toggle-line text-base'} />
                    </button>
                    <button
                      onClick={() => openEdit(m)}
                      title="Editar"
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm" />
                    </button>
                    {!isPrincipal && (s?.total_funcionarios ?? 0) === 0 && (
                      <button
                        onClick={() => setConfirmDeleteId(m.id)}
                        title="Eliminar módulo"
                        className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Guia de migração */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
            <i className="ri-database-2-line text-amber-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Guia de Migração de Dados</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Como importar dados de outro Supabase para um novo módulo</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {[
            { step: '1', title: 'Criar o novo módulo aqui', desc: 'Clica em "Novo Módulo" acima, preenche o nome e sigla e guarda.', icon: 'ri-add-circle-line', color: 'bg-zinc-900 text-white' },
            { step: '2', title: 'Exportar dados do outro Supabase', desc: 'No painel do outro Supabase → Table Editor → seleciona a tabela (ex: funcionarios) → clica em "Export" → CSV. Repete para: funcionarios, registos_siadap, historico_progressoes, progressoes.', icon: 'ri-download-cloud-line', color: 'bg-amber-100 text-amber-700' },
            { step: '3', title: 'Obter o ID do novo módulo', desc: 'Após criar o módulo, vai ao Supabase → Table Editor → tabela "municipios" → copia o UUID do novo módulo.', icon: 'ri-key-line', color: 'bg-zinc-100 text-zinc-600' },
            { step: '4', title: 'Editar os CSVs exportados', desc: 'Abre cada CSV num editor (Excel ou Google Sheets). Adiciona uma coluna "municipio_id" e preenche todas as linhas com o UUID copiado no passo 3.', icon: 'ri-file-edit-line', color: 'bg-zinc-100 text-zinc-600' },
            { step: '5', title: 'Importar os CSVs neste Supabase', desc: 'Neste Supabase → Table Editor → seleciona a tabela → clica em "Import data from CSV" → carrega o ficheiro editado. Repete para cada tabela.', icon: 'ri-upload-cloud-line', color: 'bg-emerald-100 text-emerald-700' },
            { step: '6', title: 'Criar utilizador para o novo módulo', desc: 'Clica no ícone de utilizador ao lado do módulo na lista acima, ou usa o botão "Novo Utilizador". Esse utilizador só verá os dados do seu módulo.', icon: 'ri-user-add-line', color: 'bg-zinc-100 text-zinc-600' },
          ].map((item) => (
            <div key={item.step} className="flex gap-4">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-sm font-bold ${item.color}`}>
                {item.step}
              </div>
              <div className="flex-1 pt-0.5">
                <p className="text-[13px] font-semibold text-zinc-800 mb-0.5">{item.title}</p>
                <p className="text-[12px] text-zinc-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal confirmação de eliminação */}
      {confirmDeleteId && (() => {
        const mun = municipios.find((m) => m.id === confirmDeleteId);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => !deletingId && setConfirmDeleteId(null)}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                  <i className="ri-delete-bin-line text-red-500 text-xl" />
                </div>
                <h3 className="text-[15px] font-semibold text-zinc-800 mb-2">Eliminar módulo?</h3>
                <p className="text-[13px] text-zinc-500 mb-6">
                  Tens a certeza que queres eliminar <strong className="text-zinc-700">{mun?.nome}</strong>? Esta ação não pode ser revertida.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    disabled={!!deletingId}
                    className="flex-1 py-2.5 text-sm font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleDelete(confirmDeleteId)}
                    disabled={!!deletingId}
                    className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {deletingId ? <><i className="ri-loader-4-line animate-spin" />A eliminar...</> : <><i className="ri-delete-bin-line" />Eliminar</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal formulário município */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => !saving && setShowForm(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h3 className="text-[15px] font-semibold text-zinc-800">
                {editingId ? 'Editar Módulo' : 'Novo Módulo'}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Nome <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                  placeholder="ex: Gestão da Formação"
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-400 transition-colors"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Sigla <span className="text-zinc-400 font-normal">(opcional)</span></label>
                <input
                  type="text"
                  value={form.sigla}
                  onChange={(e) => setForm((f) => ({ ...f, sigla: e.target.value.toUpperCase().slice(0, 6) }))}
                  placeholder="ex: GF"
                  maxLength={6}
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-400 transition-colors"
                />
              </div>
              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                <input type="checkbox" id="ativo-check" checked={form.ativo} onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))} className="cursor-pointer" />
                <label htmlFor="ativo-check" className="text-sm text-zinc-700 cursor-pointer">Módulo ativo</label>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => setShowForm(false)} disabled={saving} className="flex-1 py-2.5 text-sm font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <><i className="ri-loader-4-line animate-spin" />A guardar...</> : <><i className="ri-save-line" />{editingId ? 'Guardar' : 'Criar'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal criar utilizador */}
      {showUserForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => !savingUser && setShowUserForm(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h3 className="text-[15px] font-semibold text-zinc-800">Criar Utilizador</h3>
              <button onClick={() => setShowUserForm(false)} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Módulo <span className="text-red-500">*</span></label>
                <select
                  value={userForm.municipio_id}
                  onChange={(e) => setUserForm((f) => ({ ...f, municipio_id: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 cursor-pointer"
                >
                  <option value="">Selecionar módulo...</option>
                  {municipios.map((m) => (
                    <option key={m.id} value={m.id}>{m.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Nome completo <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm((f) => ({ ...f, full_name: e.target.value }))}
                  placeholder="ex: Sofia Vaz"
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 transition-colors"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="ex: sofia@exemplo.pt"
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Password <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Perfil</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value as 'admin' | 'viewer' }))}
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 cursor-pointer"
                >
                  <option value="admin">Administrador</option>
                  <option value="viewer">Visualizador</option>
                </select>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => setShowUserForm(false)} disabled={savingUser} className="flex-1 py-2.5 text-sm font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">Cancelar</button>
              <button onClick={handleCreateUser} disabled={savingUser} className="flex-1 py-2.5 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2">
                {savingUser ? <><i className="ri-loader-4-line animate-spin" />A criar...</> : <><i className="ri-user-add-line" />Criar Utilizador</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}